import { useQuery, useMutation, useQueryClient } from "react-query";

import {
  HttpClientError,
  QueryKeys,
  getQuerySettings,
  IQuerySettings,
} from "@luxon/models";
import { useHttpClient, useSnackbar } from "@luxon/hooks";
import { IDefaultResponseMessage, IExportSingleBillboardMetricsRequest } from "@luxon/interfaces";
import { ICreateBackgroundDataExportJobRequest, IDownloadBackgroundDataExportJobResult } from "@luxon/models/reports";
import { BACKGROUND_DATA_EXPORT_TYPE, IBackgroundDataExportJob } from "@luxon/models/background-data-export-jobs";

enum BackgroundDataExportJobsQueryKey {
  TargetList = 'TargetList'
}

const BASE_URL = '/v1/background-data-export-jobs';

export const useGetBackgroundDataExportJobsForTarget = (targetId: string, exportTypes: BACKGROUND_DATA_EXPORT_TYPE[], settings?: IQuerySettings<IBackgroundDataExportJob[]>) => {
  const { GET } = useHttpClient();

  const querySettings = getQuerySettings(settings);
  return useQuery<IBackgroundDataExportJob[], HttpClientError>(
    [QueryKeys.BackgroundDataExportJobs, BackgroundDataExportJobsQueryKey.TargetList, targetId, exportTypes],
    () => {
      return GET<IBackgroundDataExportJob[]>(`${BASE_URL}/${targetId}`, {
        exportTypes
      });
    },
    {
      ...querySettings,
      enabled: querySettings.enabled && Boolean(targetId)
    }
  );
}

export const useCreateSingleBillboardMetricsBackgroundDataExportJobMutation = (settings?: IQuerySettings<IBackgroundDataExportJob, ICreateBackgroundDataExportJobRequest<IExportSingleBillboardMetricsRequest>>) => {
  const { POST } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const runRequest = (request: ICreateBackgroundDataExportJobRequest<IExportSingleBillboardMetricsRequest>) => {
    return POST<IBackgroundDataExportJob>(`${BASE_URL}/single-billboard-metrics`, request);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IBackgroundDataExportJob, HttpClientError, ICreateBackgroundDataExportJobRequest<IExportSingleBillboardMetricsRequest>>(runRequest, {
    onSuccess: (response, input) => {
      showSnackbar('Data export was queued successfully');

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.length >= 3 &&
          query.queryKey[0] === QueryKeys.BackgroundDataExportJobs &&
          query.queryKey[1] === BackgroundDataExportJobsQueryKey.TargetList && 
          query.queryKey[2] === input.targetId
      });

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
}

export const useDownloadBackgroundDataExportJobMutation = (settings?: IQuerySettings<IDefaultResponseMessage, IDownloadBackgroundDataExportJobResult>) => {
  const { POST } = useHttpClient();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const runRequest = ({ exportJobId }: IDownloadBackgroundDataExportJobResult) => {
    return POST<IDefaultResponseMessage>(`${BASE_URL}/${exportJobId}/download`);
  };

  const querySettings = getQuerySettings(settings);
  return useMutation<IDefaultResponseMessage, HttpClientError, IDownloadBackgroundDataExportJobResult>(runRequest, {
    onSuccess: (response, input) => {
      showSnackbar('File download will being shortly');

      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.length >= 3 &&
          query.queryKey[0] === QueryKeys.BackgroundDataExportJobs &&
          query.queryKey[1] === BackgroundDataExportJobsQueryKey.TargetList && 
          query.queryKey[2] === input.targetId
      });

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input);
      }
    }
  });
}
